import _ from 'lodash';
import memoize from 'memoize-weak';

import {
  AuthTypes,
  IS_STALE_AUTH,
  SELECTED_API,
  TestingStatuses,
} from 'app/auth/utils/constants';

export const getAuths = state => {
  return _.get(state, ['auth', 'auths']);
};

export const isAuthDataStale = auth => {
  return _.get(auth, IS_STALE_AUTH, false);
};

export const isAuthStaleAfterPolling = (auth, authState = {}) => {
  if (
    !_.isEmpty(authState) &&
    !!authState.testingStatus &&
    authState.testingStatus !== TestingStatuses.INITIAL &&
    authState.testingStatus !== TestingStatuses.TESTING
  ) {
    return !!authState.testingError;
  }

  return isAuthDataStale(auth);
};

export const hasStaleAuths = state =>
  _.filter(getAuths(state), isAuthDataStale).length > 0;

export const getAuthsStates = state => {
  return _.get(state, ['auth', 'authStates']);
};

export const getAuthsBySelectedApi = (selectedApi, state) => {
  return _.values(getAuths(state)).filter((auth = {}) => {
    return auth.selected_api === selectedApi && !auth.destination_selected_api;
  });
};

export const hasLoadedAuths = state => {
  return !!getAuths(state);
};

export const isLoadingAuths = state => {
  return _.get(state, ['auth', 'isLoading']);
};

export const getAuthData = (id, state) => {
  return _.get(getAuths(state), id);
};

export const getAuthApi = (id, state) => {
  return _.get(getAuthData(id, state), SELECTED_API);
};

export const selectIsStaleAuth = (id, state) => {
  if (!id) {
    return false;
  }
  return isAuthDataStale(getAuthData(id, state));
};

const getAuthStateById = (authStates, id) => _.get(authStates, id);

export const getAuthState = (id, state) => {
  return getAuthStateById(getAuthsStates(state), id);
};

export const findAuthById = (auths, id) =>
  _.find(auths, auth => auth.id === id);

export const isAuthDataShared = auth => {
  return _.get(auth, 'is_shared', false);
};

export const isAuthDataRestricted = auth => {
  return !auth;
};

export const authDataLastChanged = auth => {
  return _.get(auth, 'lastchanged');
};

export const isAuthShared = (id, state) => {
  return isAuthDataShared(getAuthData(id, state));
};

export const isAuthRestricted = (id, state) => {
  return isAuthDataRestricted(getAuthData(id, state));
};

export const numZapsForAuth = (id, state) => {
  return _.get(getAuthState(id, state), 'numZaps', 0);
};

// This should be used in places where the auths collection was threaded in as a prop, instead of relying on a Container
// component that uses selectors to assemble the flat data needed for a component.
// This method allows us to re-use the logic of our selectors in those scenarios, where we don't have access to `state`.
//
// Usage example:
// `const isRestrictedAccount = isAuthRestricted(authId, makeFakeAuthState(auths));`
//
// It also makes it easy to clean up its usage in the future, because we just have to grep for 'makeFakeAuthState' to
// find all the places where we're still not properly using entities and state containers.
export const makeFakeAuthState = auths => ({ auth: { auths } });

export const computeAuthType = authData => {
  if (isAuthDataRestricted(authData)) {
    return AuthTypes.RESTRICTED;
  }
  return isAuthDataShared(authData) ? AuthTypes.SHARED : AuthTypes.PRIVATE;
};

const EMPTY_AUTHS_WITH_STALE_DATA = {};

const getAuthsMapWithStaleData = memoize((auths, authStates) =>
  _.reduce(
    auths,
    (acc, auth, authId) => {
      const authState = getAuthStateById(authStates, authId);
      const isStale = isAuthStaleAfterPolling(auth, authState);

      return {
        ...acc,
        [auth.id]: {
          ...auth,
          is_stale: isStale,
        },
      };
    },
    EMPTY_AUTHS_WITH_STALE_DATA
  )
);

export const selectAuthsMapWithStaleData = state =>
  getAuthsMapWithStaleData(state.auth.auths, getAuthsStates(state));
